import { useState, useEffect, FormEvent } from 'react';
import { api, ApiError } from '../api/client';
import '../team/TeamLogin.css';

interface TeamLeadLoginProps {
  onLogin: () => void;
}

export function TeamLeadLogin({ onLogin }: TeamLeadLoginProps): JSX.Element {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [configured, setConfigured] = useState<boolean | null>(null);

  useEffect(() => {
    fetch('/api/team-lead/status')
      .then(async (r) => {
        if (!r.ok) {
          setConfigured(true);
          return;
        }
        const body = (await r.json()) as { data?: { configured?: boolean } };
        setConfigured(body.data?.configured ?? true);
      })
      .catch(() => setConfigured(true));
  }, []);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api<{ role: string }>('/api/team-lead/login', {
        method: 'POST',
        body: JSON.stringify({ password }),
      });
      onLogin();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'TEAM_LEAD_PASSWORD_NOT_SET') {
          setError(
            'This page is not set up yet. Ask the admin to configure the team-lead password.',
          );
          setConfigured(false);
        } else if (err.code === 'TOO_MANY_REQUESTS') {
          setError('Too many attempts. Please wait a few minutes and try again.');
        } else if (err.code === 'UNAUTHORIZED') {
          setError('Wrong password. Please try again.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Connection error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="team-login-container">
      <div className="team-login-card">
        <div className="team-login-logo">AP</div>
        <h1>Team Lead</h1>
        <p>Inventory Pull and License Assignment access.</p>

        {configured === false && (
          <div className="team-login-info">
            This page is not set up yet. Ask the admin to configure the team-lead password.
          </div>
        )}

        <form className="team-login-form" onSubmit={handleSubmit}>
          {error && <div className="team-login-error">{error}</div>}
          <div className="team-login-field">
            <label htmlFor="team-lead-password">Password</label>
            <input
              id="team-lead-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              autoFocus
              required
              disabled={configured === false}
            />
          </div>
          <button
            type="submit"
            className="team-login-button"
            disabled={loading || configured === false}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
